import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check as CheckIcon, ChevronDown as ChevronDownIcon, Clock as ClockIcon, Tag as TagIcon, Truck as TruckIcon, Zap as ZapIcon } from "lucide-react";
import JourneyGroup from "./JourneyGroup";
import { ASSUMED_TAT_DAYS, MODE_ICON, MODE_STYLE, carrierAccent, estDeliveryDate, fmtInr, formatCarrier, initialsOf } from "./tokens";

/* The manual courier list: live rates as a radio group — glossy carrier monograms, mode chips,
   Cheapest / Fastest pills (ties get the tag), a "by <date>" ETA, and the price. Render-only:
   the page owns the sorting, the serviceability rule and the selection. */
export default function CourierPicker({
  loading, sortedOptions, serviceableOptions, serviceableDisplay, unserviceableOptions,
  showUnserviceable, onToggleUnserviceable, courierSort, onSortChange, courierRoute,
  cheapestPrice, fastestTat, selected, onSelect, serviceableOf, paymentLabel,
}) {
  return (
    <JourneyGroup
      icon={TruckIcon}
      title="Choose a courier"
      done={Boolean(selected)}
      last
    >
    {loading ? (
      <div className="space-y-2.5" role="status" aria-label="Getting courier rates">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-3.5 rounded-2xl border border-[#EDF1F7] p-3.5">
            <div className="h-11 w-11 flex-shrink-0 animate-pulse rounded-xl bg-slate-100" />
            <div className="flex-1 space-y-2"><div className="h-3.5 w-1/3 animate-pulse rounded bg-slate-100" /><div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" /></div>
            <div className="h-4 w-16 animate-pulse rounded bg-slate-100" />
          </div>
        ))}
        <p className="pt-0.5 text-[13px] font-medium text-slate-500">Fetching live rates for this route…</p>
      </div>
    ) : sortedOptions.length > 0 ? (
      <>
        {/* Toolbar: how many, where to, and how to sort */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <p className="text-[12.5px] text-slate-500">
            <b className="font-semibold text-slate-800">{serviceableOptions.length}</b> {serviceableOptions.length === 1 ? "courier" : "couriers"} available
            {courierRoute && <> · <span className="font-medium text-slate-600 tabular-nums">{courierRoute}</span></>}
          </p>
          {serviceableOptions.length > 1 && (
            <div className="inline-flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5 text-[12px] font-semibold" role="tablist" aria-label="Sort couriers">
              {[["cheapest", "Cheapest"], ["fastest", "Fastest"]].map(([k, label]) => (
                <button key={k} type="button" role="tab" aria-selected={courierSort === k} onClick={() => onSortChange(k)}
                  className={`rounded-md px-2.5 py-1 transition-colors ${courierSort === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-2.5" role="radiogroup" aria-label="Courier">
          <AnimatePresence initial={false} mode="popLayout">
          {(serviceableDisplay.length ? [...serviceableDisplay, ...(showUnserviceable ? unserviceableOptions : [])] : sortedOptions).map((option) => {
            const isServiceable = serviceableOf(option);
            const isSelected = selected === option.carrier_partner_public_name;
            const name = option.carrier_partner_public_name;
            const { carrier, mode } = formatCarrier(name);
            const ModeIcon = mode ? MODE_ICON[mode] : null;
            const accent = carrierAccent(carrier); // per-carrier, so Delhivery Air & Surface share a colour
            const isCheapest = isServiceable && cheapestPrice != null && Math.abs(Number(option.total_amount_charged_after_tax) - cheapestPrice) < 0.005;
            // a lane without a turnaround time counts as the assumed figure it displays, tag included
            const effectiveTat = Number.isFinite(Number(option.max_tat_in_days)) ? Number(option.max_tat_in_days) : ASSUMED_TAT_DAYS;
            const isFastest = isServiceable && fastestTat != null && effectiveTat === fastestTat;
            const price = Number(option.total_amount_charged_after_tax);
            const date = estDeliveryDate(option.max_tat_in_days);
            return (
              <motion.button
                key={name}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
                whileTap={isServiceable ? { scale: 0.99 } : undefined}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-disabled={!isServiceable}
                onClick={() => { if (isServiceable) onSelect(name); }}
                className={`relative flex w-full items-center gap-3.5 rounded-2xl border p-3.5 text-left transition-[border-color,box-shadow,background-color] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${
                  isSelected
                    ? "border-[#3877FC] bg-white shadow-[0_4px_14px_-8px_rgba(15,23,42,0.25)]"
                    : isServiceable
                      ? "border-[#E7ECF3] bg-white hover:border-[#C4D2E8] hover:shadow-[0_8px_20px_-12px_rgba(15,23,42,0.28)]"
                      : "cursor-default border-[#EDF1F7] bg-slate-50/40"
                }`}
              >
                {/* Selected: a springy accent spine anchors the choice */}
                <AnimatePresence>{isSelected && (
                  <motion.span
                    key="spine"
                    initial={{ opacity: 0, scaleY: 0.35 }}
                    animate={{ opacity: 1, scaleY: 1 }}
                    exit={{ opacity: 0, scaleY: 0.35 }}
                    transition={{ type: "spring", stiffness: 420, damping: 30 }}
                    className="absolute left-0 inset-y-0 my-auto h-7 w-[3px] rounded-r-full bg-[#3877FC]"
                  />
                )}</AnimatePresence>

                <span className={`grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl text-[14px] font-extrabold ${!isServiceable ? "opacity-45 grayscale" : ""}`}
                  style={{ backgroundImage: `radial-gradient(circle at 30% 22%, ${accent}33, transparent 62%), linear-gradient(150deg, ${accent}1F, ${accent}0A)`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}2E, 0 1px 2px rgba(15,23,42,0.06)` }}>
                  {initialsOf(carrier)}
                </span>

                {/* Name + mode, then ranking + ETA — two tight lines */}
                <span className={`min-w-0 flex-1 ${!isServiceable ? "opacity-60" : ""}`}>
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="whitespace-nowrap text-[14.5px] font-bold text-slate-900">{carrier}</span>
                    {mode && (
                      <span className={`inline-flex flex-shrink-0 items-center gap-1 rounded-md px-1.5 py-[2px] text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ${MODE_STYLE[mode] || "bg-slate-100 text-slate-500 ring-slate-200/70"}`}>
                        {ModeIcon && <ModeIcon className="h-3 w-3" strokeWidth={2.25} />}
                        {mode}
                      </span>
                    )}
                    {isCheapest && (
                      <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-gradient-to-b from-emerald-500 to-emerald-600 px-2 py-[3px] text-[10px] font-bold uppercase tracking-wide text-white shadow-[0_1px_2px_rgba(5,150,105,0.45)]"><TagIcon className="h-3 w-3" strokeWidth={2.5} /> Cheapest</span>
                    )}
                    {isFastest && (
                      <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-gradient-to-b from-[#3877FC] to-[#2A59BD] px-2 py-[3px] text-[10px] font-bold uppercase tracking-wide text-white shadow-[0_1px_2px_rgba(56,119,252,0.45)]"><ZapIcon className="h-3 w-3" fill="currentColor" strokeWidth={0} /> Fastest</span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-[12.5px]">
                    {isServiceable ? (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-slate-500">
                        <ClockIcon className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                        {date
                          ? <span>By <b className="font-semibold text-slate-700">{date}</b><span className="hidden sm:inline"> · {option.max_tat_in_days} day{option.max_tat_in_days === 1 ? "" : "s"}</span></span>
                          : Number.isFinite(Number(option.max_tat_in_days))
                            ? <span>Up to {option.max_tat_in_days} day{option.max_tat_in_days === 1 ? "" : "s"} from pickup</span>
                            // no turnaround time on this rate: an assumed figure, and "Usually" is the tell
                            : <span title="The courier shared no delivery time for this lane; this is the usual figure">Usually {ASSUMED_TAT_DAYS} days from pickup</span>}
                      </span>
                    ) : (
                      <span className="font-medium text-rose-500">Not serviceable for {paymentLabel} on this route</span>
                    )}
                  </span>
                </span>

                {/* Price + selector, vertically centred against the block */}
                <span className="flex flex-shrink-0 items-center gap-3">
                  <span className="text-right">
                    <span className={`block text-[17px] font-bold leading-tight tabular-nums transition-colors ${isServiceable ? (isSelected ? "text-[#2A59BD]" : "text-slate-900") : "text-slate-400"}`}>{fmtInr(price)}</span>
                    <span className="mt-0.5 block whitespace-nowrap text-[10.5px] font-medium text-slate-400">incl. GST</span>
                  </span>
                  <span className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-full border-2 transition-colors ${isSelected ? "border-[#3877FC] bg-[#3877FC]" : isServiceable ? "border-slate-300" : "border-transparent"}`}>
                    <AnimatePresence>{isSelected && (
                      <motion.span key="ck" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} transition={{ type: "spring", stiffness: 500, damping: 30 }}>
                        <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3.5} />
                      </motion.span>
                    )}</AnimatePresence>
                  </span>
                </span>
              </motion.button>
            );
          })}
          </AnimatePresence>
        </div>

        {serviceableOptions.length > 0 && unserviceableOptions.length > 0 && (
          <button
            type="button"
            onClick={onToggleUnserviceable}
            className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-slate-400 transition-colors hover:text-slate-600"
          >
            {showUnserviceable ? "Hide" : "Show"} {unserviceableOptions.length} not serviceable on this route
            <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${showUnserviceable ? "rotate-180" : ""}`} />
          </button>
        )}
      </>
    ) : (
      <div className="flex flex-col items-center gap-1.5 rounded-2xl border border-dashed border-slate-300 px-6 py-10 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-100 text-slate-400"><TruckIcon className="h-6 w-6" /></span>
        <p className="mt-2 text-[14.5px] font-semibold text-slate-800">No courier rates for this route</p>
        <p className="text-[13px] text-slate-500">Check both pincodes and the package, or switch to smart allocation.</p>
      </div>
    )}
    </JourneyGroup>
  );
}
